import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import Button from '../../../components/common/Button'
import ListPage from '../../../components/templates/ListPage'
import Text from '../../../components/common/Text'
import { arrayMove } from '@dnd-kit/sortable'
import { DragEndEvent } from '@dnd-kit/core'
import { ColumnsProps } from '../../../components/common/TableDnd'
import { useVisible } from '../../../../utils/custom-hooks'
import { useGetAllCategory, useUpdateCategories } from '../../../services/category/category-query'
import { CategoryItemType } from '../../../services/category/category-type'
import CreateOrEditModal from './CreateOrEditModal'
import CategoryTableTopbar from './CategoryTableTopbar'
import CategoryTableBottombar from './CategoryTableBottombar'
import ConfirmModal from './ConfirmModal'
import { Authorize } from '../../../components/Authorize'
import { ROLE } from '../../../services/user/user-typed'

export type EditCategoryProductItemType = Omit<CategoryItemType, 'categoryProducts'> & {
  productIds: string[]
}

const CategoryList = () => {
  const { data: categoryList } = useGetAllCategory()
  const { mutate: onUpdateCategories } = useUpdateCategories()
  const [rows, setRows] = useState<EditCategoryProductItemType[]>([])
  const [initialRows, setInitialRows] = useState<EditCategoryProductItemType[]>([])

  useEffect(() => {
    if (categoryList?.items) {
      const initialData = categoryList.items.map(item => ({
        ...item,
        productIds: item.categoryProducts?.map(product => product.productId) || [],
      }))
      setInitialRows(initialData)
      setRows(initialData)
    }
  }, [categoryList])

  const [editRank, setEdit] = useState(false)
  const onConfirmModalVisible = useVisible()
  const [editingItem, setEditingItem] = useState<EditCategoryProductItemType | null>(null)

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (active.id !== over?.id) {
      const oldIndex = rows.findIndex(item => item.id === active.id)
      const newIndex = rows.findIndex(item => item.id === over?.id)
      const newRows = arrayMove(rows, oldIndex, newIndex).map((item, idx) => ({
        ...item,
        rank: idx + 1,
      }))
      setRows(newRows)
    }
  }

  const handleDeleteItem = useCallback(
    (item: CategoryItemType) => {
      setRows(prev =>
        prev
          .filter(row => row.id !== item.id)
          .map((row, index) => ({
            ...row,
            rank: index + 1,
          })),
      )

      if (rows.length <= 1 && categoryList?.items.length === 0) {
        setEdit(false)
      }
    },
    [categoryList, rows.length],
  )

  const columns: ColumnsProps = useMemo(
    () => [
      { title: 'Rank', dataIndex: 'rank' },
      { title: 'Name', dataIndex: 'name', render: (text: string) => <Text>{text}</Text> },
      {
        title: 'Category Key',
        dataIndex: 'categoryName',
        render: (text: string) => <Text>{text || '-'}</Text>,
      },
      {
        title: '',
        dataIndex: 'action',
        render: (_: string, record: EditCategoryProductItemType) =>
          !editRank ? (
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button onClick={() => setEditingItem(record)}>View</Button>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <Button variant="warning" onClick={() => setEditingItem(record)}>
                Edit
              </Button>
              <Button variant="danger" onClick={() => handleDeleteItem(record)}>
                Delete
              </Button>
            </div>
          ),
      },
    ],
    [editRank, handleDeleteItem],
  )

  const handleOpenConfirmModal = useCallback(() => {
    onConfirmModalVisible.open()
  }, [onConfirmModalVisible])

  const onEditRank = useCallback(() => {
    setEdit(true)
  }, [])

  const handleSubmit = useCallback(() => {
    // Include id so backend can distinguish updates vs creates
    const payload = rows.map(({ rank, name, categoryName, limit, productIds }) => ({
      rank,
      name,
      categoryName,
      limit,
      categoryProducts: productIds?.map((productId, index) => ({
        productId: productId,
        rank: index + 1,
      })),
    }))

    onUpdateCategories(
      { categories: payload },
      {
        onSuccess: () => {
          onConfirmModalVisible.close()
          setEdit(false)
        },
      },
    )
  }, [onConfirmModalVisible, onUpdateCategories, rows])

  const onCancelEdit = useCallback(() => {
    setEdit(false)
    setEditingItem(null)
    setRows(initialRows)
  }, [initialRows])

  const isNotHaveCategory = useMemo(() => {
    return !categoryList || categoryList.items.length < 1
  }, [categoryList])

  const handleCreate = useCallback(() => {
    if (isNotHaveCategory) {
      setEdit(true)
    }
    setEditingItem({
      id: `new-${Date.now()}`,
      name: '',
      categoryName: '',
      rank: rows.length + 1,
      productIds: [],
    })
  }, [isNotHaveCategory, rows.length])

  const handleSaveItem = useCallback(
    (value: EditCategoryProductItemType) => {
      setRows(prev => {
        if (editingItem) {
          if (editingItem.id.startsWith('new-')) {
            // If new item already exists in rows, update it
            const exists = prev.some(item => item.id === editingItem.id)
            if (exists) {
              return prev.map(item => (item.id === editingItem.id ? { ...item, ...value } : item))
            }
            // Otherwise add new item
            const newItem: EditCategoryProductItemType = {
              id: editingItem.id,
              name: value.name,
              categoryName: value.categoryName,
              limit: value.limit,
              rank: prev.length + 1,
              productIds: value.productIds,
            }
            return [...prev, newItem]
          } else {
            return prev.map(item => (item.id === editingItem.id ? { ...item, ...value } : item))
          }
        }
        return prev
      })
      setEditingItem(null)
    },
    [editingItem],
  )

  const handleCloseModal = useCallback(() => {
    setEditingItem(null)
    if (isNotHaveCategory && rows.length < 1) {
      setEdit(false)
    }
  }, [isNotHaveCategory, rows.length])
  return (
    <Fragment>
      <Authorize role={ROLE.MANAGER}>
        <ListPage
          topbar={
            <Authorize role={ROLE.ADMIN}>
              <CategoryTableTopbar
                editMode={editRank || isNotHaveCategory}
                onCreate={handleCreate}
                onEdit={onEditRank}
              />
            </Authorize>
          }
          tableProps={{
            columns,
            data: editRank ? rows : initialRows,
          }}
          onDragEnd={editRank ? onDragEnd : undefined}
          showPaginate={false}
          bottombar={
            <Authorize role={ROLE.ADMIN}>
              <CategoryTableBottombar
                editMode={editRank}
                onSubmit={handleOpenConfirmModal}
                onCancel={onCancelEdit}
              />
            </Authorize>
          }
        />
      </Authorize>

      <Authorize role={ROLE.MANAGER}>
        <ConfirmModal
          open={onConfirmModalVisible.visible}
          onClose={onConfirmModalVisible.close}
          onSubmit={handleSubmit}
        />

        <CreateOrEditModal
          key={editingItem?.id || 'new'}
          open={!!editingItem}
          onClose={handleCloseModal}
          initialValues={editingItem || undefined}
          onSubmit={handleSaveItem}
          isView={!editRank}
        />
      </Authorize>
    </Fragment>
  )
}

export default CategoryList
